// 010A — Laboratorio Clínico Solicitud (Fase 7, Tanda 14, ver PLAN.md). Transcripción del PDF oficial del Anexo 1 MSP:
// hoja 1 idx 40 (listado de exámenes) y hoja 2 idx 41 (VIH/ITS y tuberculosis), footer `SNS-MSP/HCU-form.010A/2021`.
// Informe de brechas en `docs/hce-fase7/brechas-010-012-013.md`.
//
// Retirado de la UI por no estar impreso: los 7 bloques de ~24 exámenes de la memoria ("Perfil lipídico", "Perfil hepático",
// "Electrolitos", "Hepatitis C", "Cultivo y antibiograma" como bloques propios...) y la prioridad "Control" (el papel
// solo tiene Urgente / Rutina). Decisiones ante lo que el papel no resuelve: "Anticuerpos anticore Ig-G (HBcAG)" está
// impreso dos veces (Inmunología, columnas 1 y 2) y se conservan las dos casillas; el "*" de "Nitrato reductasa (GRIESS)"
// no tiene llamada visible y no se interpreta; las casillas en blanco del papel se capturan como filas de texto libre por grupo (`LIBRES_010A`; antes solo Hematología tenía un área de texto, `hematologiaOtros`, ya retirada: lo guardado queda en el PDF firmado); el
// nombre impreso "Zhiel-Nielssen" se transcribe como "Ziehl-Neelsen" (igual que otras erratas menores del papel: "Creatina", "Hematozooario"); la firma de quien toma la muestra es manuscrita.
//
// FHIR (consumidor: `LabsTab` lee `ServiceRequest.code.text`; `SubirDocumento` exige `status` active): un solo
// `ServiceRequest` `active`/`order`, categoría laboratorio, con `priority`, `occurrenceDateTime` (fecha de toma) y el
// resumen de lo marcado en `code.text`. Todo lo demás va como `Par[]` en el PDF firmado.
import type { CampoSimple, OpcionCampo } from './camposSimplesDatos';

export interface GrupoExamenes {
  readonly clave: string;
  readonly etiqueta: string;
  readonly items: readonly string[];
}

export const GRUPOS_EXAMENES_010A: readonly GrupoExamenes[] = [
  {
    clave: 'hematologia',
    etiqueta: 'Hematología',
    items: [
      'Biometría hemática', 'Hematocrito (HCTO)', 'Hemoglobina (HB)', 'Plaquetas', 'Reticulocitos', 'Velocidad de eritrosedimentación', 'Hierro sérico', 'Fijación hierro',
      'Porcentaje saturación transferrina', 'Transferrina', 'Ferritina', 'Fragilidad osmótica eritrocitaria', 'Metabisulfito', 'Hematozoario', 'Investigación de Leishmania',
      'Eosinófilo en moco nasal', 'Frotis sangre periférica', 'Ácido fólico', 'Vitamina B12',
    ],
  },
  {
    clave: 'coagulacion',
    etiqueta: 'Coagulación y hemostasia',
    items: ['Tiempo de protrombina (TP)', 'Tiempo de tromboplastina parcial (TTP)', 'Tiempo de trombina (TT)', 'INR', 'Factor coagulación VIII', 'Factor coagulación IX', 'Factor von Willebrand', 'Fibrinógeno', 'Dímero-D', 'Identificación de inhibidores'],
  },
  {
    clave: 'quimica',
    etiqueta: 'Química sanguínea',
    items: [
      'Glucosa basal', 'Glucosa post prandial 2 horas', 'Glucosa al azar', 'Sobrecarga glucosa 75 gramos', 'Test de Sullivan (glucosa 50 gramos)', 'Urea', 'Creatinina', 'Ácido úrico',
      'Fosfatasa alcalina', 'LDH', 'AST/TGO', 'ALT/TGP', 'GGT', 'Amilasa', 'Lipasa', 'Bilirrubina total', 'Bilirrubina directa', 'Bilirrubina indirecta', 'Colesterol total',
      'Lipoproteína de alta densidad (HDL)', 'Lipoproteína de baja densidad (LDL)', 'Lipoproteína de muy baja densidad (VLDL)', 'Triglicéridos', 'Albúmina', 'Proteínas totales',
      'Hemoglobina glicosilada (HbA1c)', 'CPK total', 'Fructosamina', 'PCR cuantitativo',
    ],
  },
  {
    clave: 'inmunologia',
    etiqueta: 'Inmunología / infecciosas',
    items: [
      'Complemento C3', 'Complemento C4', 'IgA total', 'IgE total', 'IgG total', 'IgM total', 'Procalcitonina', 'IL-6', 'ANA', 'ANCA-C', 'ANCA-P', 'Anti-DNA', 'Anti-CCP', 'Anti-SM', 'Anti-RO',
      'Anti-LA', 'Anti cardiolipina IgG', 'Anti cardiolipina IgM', 'Antifosfolípidos IgG', 'Antifosfolípidos IgM', 'Factor reumatoideo (IgM)', 'sFLT1 (marcador de preeclampsia)',
      'PIGF (marcador de preeclampsia)', 'Anticuerpos anticore Ig-G (HBcAG)', 'Hepatitis A (IgM)', 'Hepatitis A total', 'Antígeno de superficie hepatitis B (HBsAg)',
      'Anticuerpos anticore Ig-G (HBcAG)', 'Anticuerpos anticore Ig-M (HBcAG)', 'Hepatitis C: HVC', 'VIH (1+2) cualitativa', 'VIH (1+2) cuantitativa', 'Herpes 1 IgG', 'Herpes 1 IgM',
      'Herpes 2 IgG', 'Herpes 2 IgM', 'Rubeola IgG', 'Rubeola IgM', 'Toxoplasma IgG', 'Toxoplasma IgM', 'Citomegalovirus IgG', 'Citomegalovirus IgM', 'Epstein Barr IgG', 'Epstein Barr IgM',
      'Dengue IgG', 'Dengue IgM', 'Clamidia IgA', 'Clamidia IgG', 'FTA-ABS',
    ],
  },
  {
    clave: 'orina',
    etiqueta: 'Orina',
    items: [
      'Elemental y microscópico (EMO)', 'Gram gota fresca', 'Osmolaridad urinaria', 'Sodio en orina parcial', 'Potasio en orina parcial', 'Cloro en orina parcial', 'Calcio urinario',
      'Fósforo en orina parcial', 'Magnesio en orina parcial', 'Glucosa en orina parcial', 'Urea en orina parcial', 'Creatinina en orina parcial', 'Nitrógeno ureico en orina parcial',
      'Ácido úrico en orina parcial', 'Proteínas en orina parcial', 'Fósforo en orina 24 h', 'Potasio en orina 24 h', 'Proteínas en orina 24 h', 'Depuración de creatinina (orina 24 h)',
      'Ácido úrico en orina 24 h', 'Calcio en orina 24 h', 'Amilasa en orina 24 h', 'Cobre en orina 24 h', 'Azúcares reductores', 'Drogas de abuso en orina', 'Albuminuria',
    ],
  },
  {
    clave: 'heces',
    etiqueta: 'Heces',
    items: [
      'Coprológico / coproparasitario', 'Coproparasitario por concentración', 'Copro seriado', 'Investigación de polimorfonucleares (PMN)', 'Sangre oculta', 'Investigación de pH', 'Rotavirus',
      'Adenovirus', 'Criptosporidium', 'Oxiuros', 'Giardia-Lamblia antígeno', 'Investigación de grasas', 'Azúcares reductores', 'Helicobacter pylori',
    ],
  },
  {
    clave: 'tumorales',
    etiqueta: 'Marcadores tumorales',
    items: ['CEA', 'AFP', 'CA 125', 'CA 15.3', 'CA 19.9', 'CA 72.4', 'PSA libre', 'PSA total', 'β2-microglobulina', 'Anti-TPO', 'Anti-TG', 'Tiroglobulina', 'HE4', 'B-HCG libre', 'B-HCG cuantitativa'],
  },
  { clave: 'liquidos', etiqueta: 'Citoquímico y bacteriológico de líquidos', items: ['Cefalorraquídeo', 'Articular / sinovial', 'Ascítico / peritoneal', 'Pleural', 'Pericárdico', 'Líquido amniótico'] },
  { clave: 'farmacos', etiqueta: 'Niveles de fármacos terapéuticos', items: ['Ácido valproico', 'Carbamazepina', 'Fenobarbital', 'Digoxina', 'Fenitoína sódica', 'Vancomicina', 'Amikacina', 'Litio'] },
  { clave: 'cardiacos', etiqueta: 'Marcadores cardíacos / vasculares', items: ['CPK total', 'CK-MB', 'CPK-NAC', 'Troponina I', 'Troponina T', 'NT-proBNP', 'Mioglobina'] },
  { clave: 'inmunosupresores', etiqueta: 'Inmunosupresores', items: ['Ciclosporina', 'Sirolimus', 'Tacrolimus', 'Everolimus'] },
  {
    clave: 'hormonas',
    etiqueta: 'Hormonas',
    items: [
      'T3', 'FT3', 'T4', 'FT4', 'TSH', 'PTH', 'FSH', 'Androstenediona', 'IGF-1', 'IGFBP3', 'B-HCG cualitativa', 'B-HCG cuantitativa', 'Hormona de crecimiento', 'Progesterona', 'Insulina', 'ACTH',
      'Prolactina', 'Vitamina D', 'Estradiol (E2)', 'LH', 'Cortisol', 'Testosterona total', 'Testosterona libre', 'DHEA-S',
    ],
  },
  { clave: 'gases', etiqueta: 'Gases y electrolitos', items: ['Na', 'K', 'Cl', 'Ca+', 'Ca', 'P', 'Mg', 'Li', 'Gasometría arterial', 'Gasometría venosa'] },
  { clave: 'transfusional', etiqueta: 'Medicina transfusional', items: ['Grupo y factor', 'Coombs directo', 'Coombs indirecto'] },
  {
    clave: 'serologia',
    etiqueta: 'Serología',
    items: ['Aglutinaciones febriles', 'ASTO', 'FR-látex', 'Dengue (PCR)', 'Chlamydia (PCR)', 'Pepsinógeno', 'VDRL', 'PCR semicuantitativa', 'Malaria (PCR)', 'Sífilis (PCR)', 'Helicobacter pylori'],
  },
  {
    clave: 'microbiologia',
    etiqueta: 'Microbiología',
    items: ['Cultivo y antibiograma', 'Cristalografía', 'Gram', 'Fresco', 'Estudio micológico (KOH)', 'Cultivo micótico', 'Investigación Paragonimus spp', 'Investigación Histoplasma spp', 'Coloración Ziehl-Neelsen'],
  },
];

/**
 * Casillas en blanco que el papel deja en cada grupo (celdas sin rótulo impreso, contadas en el escaneo hoja 1 idx 40: filas × columnas menos las
 * casillas rotuladas). Cada una se ofrece como una fila de texto libre para escribir el examen a mano; sale en el `Par[]` y en `code.text` junto a
 * los marcados. Coagulación, biología molecular (ya es un área de texto) y los demás grupos sin espacio libre no aparecen.
 */
export const LIBRES_010A: Readonly<Record<string, number>> = {
  hematologia: 3, quimica: 3, inmunologia: 3, orina: 2, heces: 2, tumorales: 3, liquidos: 3, farmacos: 2, cardiacos: 1,
  inmunosupresores: 2, hormonas: 2, gases: 2, transfusional: 3, serologia: 3, microbiologia: 1,
};

/** Clave de la fila libre `n` (0-based) de un grupo, en `Estado010A.libres`. */
export const claveLibre = (grupo: string, n: number): string => `${grupo}:libre${n}`;

/** Exámenes de Microbiología con texto libre "de ...": escribirlo cuenta como solicitarlo (el texto no se pierde si la casilla quedó sin marcar). */
export const EXAMENES_CON_TEXTO_010A: Readonly<Record<string, readonly string[]>> = {
  'Estudio micológico (KOH)': ['koh'],
  'Cultivo micótico': ['micotico1', 'micotico2', 'micotico3'],
};

/** Texto libre impreso dentro de Microbiología y de Hematología / Biología molecular. */
export const CAMPOS_MICROBIOLOGIA_010A: readonly CampoSimple[] = [
  { clave: 'muestra', etiqueta: 'Microbiología — muestra' },
  { clave: 'sitio', etiqueta: 'Microbiología — sitio anatómico' },
  { clave: 'koh', etiqueta: 'Estudio micológico (KOH) de' },
  { clave: 'micotico1', etiqueta: 'Cultivo micótico de — 1' },
  { clave: 'micotico2', etiqueta: 'Cultivo micótico de — 2' },
  { clave: 'micotico3', etiqueta: 'Cultivo micótico de — 3' },
];
export const CAMPOS_TEXTOS_LIBRES_010A: readonly CampoSimple[] = [
  { clave: 'biologiaMolecular', etiqueta: 'Biología molecular y genética', tipo: 'area' },
];

const opciones = (...e: readonly string[]): readonly OpcionCampo[] => e.map((x) => ({ valor: x, etiqueta: x }));
const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });

export const CAMPOS_SERVICIO_010A: readonly CampoSimple[] = [
  { clave: 'servicio', etiqueta: 'Servicio', tipo: 'select', opciones: opciones('Emergencia', 'Consulta externa', 'Hospitalización') },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'sala', etiqueta: 'Sala' },
  { clave: 'cama', etiqueta: 'Cama' },
  { clave: 'prioridad', etiqueta: 'Prioridad', tipo: 'select', opciones: opciones('Urgente', 'Rutina') },
  { clave: 'tratamiento', etiqueta: 'Tratamiento terapéutico (especifique nombre y tiempo de administración)' },
];

export const CAMPOS_TOMA_MUESTRA_010A: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha de toma de muestra', tipo: 'fecha' },
  { clave: 'hora', etiqueta: 'Hora de toma de muestra', tipo: 'hora' },
  { clave: 'persona', etiqueta: 'Nombre y apellido de la persona que toma la muestra' },
];

// ---------- Hoja 2 ----------

export const CAMPOS_VIH_ITS_010A: readonly CampoSimple[] = [
  casilla('pruebaRapida', 'Prueba rápida'),
  casilla('elisa', 'Elisa automatizada'),
  casilla('clia', 'CLIA'),
  casilla('ifi', 'IFI'),
  casilla('cargaViral', 'Carga viral'),
  casilla('cd4', 'CD4'),
  casilla('tamizajeSifilis', 'Tamizaje de sífilis'),
  casilla('vdrl', 'VDRL'),
  casilla('hepatitisB', 'Hepatitis B (HBs-Ag)'),
];

export const CAMPOS_TB_AFECTADO_010A: readonly CampoSimple[] = [
  ...['Nuevo', 'Recaída', 'Fracaso', 'Pérdida en el seguimiento', 'PVV', 'PPL', 'Niño < 5 años', 'Sospecha de meningitis TB', 'Alta sospecha clínica y/o radiológica BK(-)'].map((e, i) => casilla(`af${i}`, e)),
  casilla('comorbilidad', 'Comorbilidad'),
  { clave: 'comorbilidadTexto', etiqueta: 'Comorbilidad — cuál' },
  ...['Contacto TBR', 'Sospecha de TB EP', 'Talento humano en salud', 'Irregularidad en la toma del tratamiento', 'Reversión', 'Embarazo', 'BK(+) al 2º mes'].map((e, i) => casilla(`afb${i}`, e)),
  casilla('condiciones', 'Condiciones especiales'),
  { clave: 'condicionesTexto', etiqueta: 'Condiciones especiales — cuáles' },
  casilla('afOtros', 'Otros'),
  { clave: 'afOtrosTexto', etiqueta: 'Otros — cuáles' },
];

export const CAMPOS_TB_ANTECEDENTES_010A: readonly CampoSimple[] = [
  casilla('sensible', 'TB sensible'),
  casilla('resistente', 'TB resistente'),
  { clave: 'tipoResistencia', etiqueta: 'Tipo de resistencia' },
];

export const CAMPOS_TB_MUESTRA_010A: readonly CampoSimple[] = [casilla('esputo', 'Esputo'), casilla('otraMuestra', 'Otro'), { clave: 'otraMuestraTexto', etiqueta: 'Otro — cuál' }];

/** Solicitud para diagnóstico: casilla, y para baciloscopía / cultivo sólido el número de diagnóstico o control con su mes. */
export const CAMPOS_TB_SOLICITUD_010A: readonly CampoSimple[] = [
  casilla('ada', 'Ada'),
  casilla('baciloscopia', 'Baciloscopía'),
  casilla('baciloscopiaDiagnostico', 'Baciloscopía — diagnóstico'),
  { clave: 'baciloscopiaDiagnosticoNo', etiqueta: 'Baciloscopía — diagnóstico No.' },
  casilla('baciloscopiaControl', 'Baciloscopía — control'),
  { clave: 'baciloscopiaControlNoMes', etiqueta: 'Baciloscopía — control No. / mes' },
  casilla('cultivoSolido', 'Cultivo medio sólido (OK)'),
  casilla('cultivoSolidoDiagnostico', 'Cultivo medio sólido — diagnóstico'),
  { clave: 'cultivoSolidoDiagnosticoNo', etiqueta: 'Cultivo medio sólido — diagnóstico No.' },
  casilla('cultivoSolidoControl', 'Cultivo medio sólido — control'),
  { clave: 'cultivoSolidoControlNoMes', etiqueta: 'Cultivo medio sólido — control No. / mes' },
  casilla('xpert', 'PCR tiempo real (Xpert/MTB/RIF)'),
  casilla('griess', 'Nitrato reductasa (GRIESS)'),
  casilla('mgit', 'Cultivo medio líquido (MGIT)'),
  casilla('genotipificacion', 'Genotipificación'),
  casilla('tipificacion', 'Tipificación'),
  casilla('psd1Solido', 'PSD 1ª línea (proporciones - medio sólido)'),
  casilla('psd1Mgit', 'PSD 1ª línea (MGIT - medio líquido)'),
  casilla('psd2Solido', 'PSD 2ª línea (proporciones - medio sólido)'),
  casilla('psd2Mgit', 'PSD 2ª línea (MGIT - medio líquido)'),
];

